<script setup>
import ServiceBox from '@/Pages/Dashboard/Support/Services/Partials/ServiceBox.vue';
import {router, usePage} from '@inertiajs/vue3';
import UpdateBillingAddressModal from '@/components/Dashboard/UpdateBillingAddressModal.vue';
import {ref, useAttrs} from 'vue';
import ConfirmationTermsAndServiceModal from "@/components/Dashboard/ConfirmationTermsAndServiceModal.vue";
import FormInput from "@/components/Shared/Form/Input/Index.vue";

const props = defineProps({
  services: {
    type: Array,
    required: true,
  },
  gettingStartedServices: {
    type: Array,
    required: true,
  },
  pricePerHour: {
    type: Number,
    required: true,
  },
  termsAndConditionsUrl: {
    type: String,
    required: true,
  },
});

const user = usePage().props.user;
const attrs = useAttrs()
const isUpdateBillingAddressModalOpen = ref(false);
const isTermsAndConditionsModalOpen = ref(false);
const hasValidBillingAddress = ref(user.has_valid_billing_address);
const hasAgreedToTerms = ref(false);
const hasAgreedToProcessing = ref(false);
const selectedServiceData = ref({});

const prePurchaseCheck = (data) => {
  selectedServiceData.value = data

  if (!hasValidBillingAddress.value) {
    isUpdateBillingAddressModalOpen.value = true;

    return;
  }
  if (!hasAgreedToTerms.value || !hasAgreedToProcessing.value) {
    isTermsAndConditionsModalOpen.value = true;
  }
}

const readyToViewTerms = () => {
  hasValidBillingAddress.value = true
  isTermsAndConditionsModalOpen.value = true;
}

const checkReadyToPurchase = () => {
  if (!hasValidBillingAddress.value) {
    return;
  }
  if (!hasAgreedToTerms.value || !hasAgreedToProcessing.value) {
    return;
  }
  processToPurchase();
}

const processToPurchase = () => {
  if (selectedServiceData.value.service === -1) // custom hourly service
    handlePurchase(selectedServiceData.value)
  else
    handlePurchaseGettingStarted(selectedServiceData.value) // package services
};

const handlePurchase = async ({service, hours, setProcessing}) => {
  router.post(
    route('dashboard.support.services.purchase'),
    {service, hours},
    {
      onStart: () => {
        setProcessing(true);
      },
      onFinish: () => {
        setProcessing(false);
      },
    },
  );
};

const handlePurchaseGettingStarted = async ({service, setProcessing}) => {
  router.post(
    route('dashboard.support.services.purchase.getting-started'),
    {service},
    {
      onStart: () => {
        setProcessing(true);
      },
      onFinish: () => {
        setProcessing(false);
      },
    },
  );
};
</script>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue';

export default {
  layout: DashboardLayout,
};
</script>

<template>
  <div class="p-5 bg-white mx-4">
    <div class="flex justify-center">
      <h2>{{ trans('__JSON__.Purchase support hours or integration packages') }}</h2>
    </div>
    <div class="flex justify-center">
      <h5
        v-html="trans('__JSON__.Purchase support hours or an integration package below. These hours and packages will be credited to your account and processed immediately. Our API experts will then contact you.')"></h5>
    </div>
    <div class="flex justify-center">
      <h5 v-html="trans('__JSON__.The services below are provided by your partner.')"></h5>
    </div>
    <div class="flex justify-center justify-content-sm-between gap">
      <service-box
        v-for="(service, index) in services"
        :key="`service-${index}`"
        :price="service.hours !== null ? service.hours * pricePerHour : null"
        :price-per-hour="pricePerHour"
        :show-hours-input="service.hours === null"
        :title="service.title"
        :sub-title="service.subtitle"
        :value="service.value"
        :errors="attrs.errors"
        @purchase="prePurchaseCheck"
      />
      <service-box
        v-for="(service, index) in gettingStartedServices"
        :key="`getting-started-service-${index}`"
        :descriptions="service.descriptions"
        :price="service.price"
        :title="service.title"
        :sub-title="service.subtitle"
        :value="service.value"
        @purchase="prePurchaseCheck"
      />
    </div>
    <div class="flex justify-center mt-4">
      <h1 class="h2">
        {{ trans('__JSON__.Do you have application specific questions?') }}
      </h1>
    </div>
    <div class="flex justify-center">
      <h4 class="h5">
        {{
          trans('__JSON__.Do you want to have specific adjustments made to your application? Please contact a partner.')
        }}
      </h4>
    </div>
    <div class="flex justify-center my-3">
      <inertia-link :href="route('dashboard.support.partners.index')" class="button">
        {{ trans('__JSON__.Find a partner') }}
      </inertia-link>
    </div>
  </div>
  <update-billing-address-modal
    v-if="isUpdateBillingAddressModalOpen"
    :user="user"
    @close="() => (isUpdateBillingAddressModalOpen = false)"
    @valid="readyToViewTerms"
  ></update-billing-address-modal>
  <ConfirmationTermsAndServiceModal
    :show-modal="isTermsAndConditionsModalOpen"
    :user="user"
    @close="() => (isTermsAndConditionsModalOpen = false)"
    @confirm="checkReadyToPurchase"
    :header="trans('__JSON__.Important information about this service')"
    content=""
  >
    <template #content>
      {{ trans('__JSON__.The services offered here are provided by an APIcenter partner. When purchasing these services, you enter into an agreement with this partner, not with APIcenter. The partner is responsible for the delivery and quality of the services, as well as for billing and support. You can discuss any questions or comments about the services directly with the partner.') }}
      <div class="termsAndServiceBoxes">
        <form-input id="terms"
                    type="checkbox"
                    v-model="hasAgreedToTerms"
                    :label-text="trans('__JSON__.I accept the :linkOpen Terms of Service :linkClose', {linkOpen: '<a href=\''+ termsAndConditionsUrl +'\' target=\'_blank\'>', 'linkClose': '</a>'})"/>

        <form-input id="processing_agreement"
                    type="checkbox"
                    v-model="hasAgreedToProcessing"
                    :label-text="trans('__JSON__.I accept the :linkOpen Processing Agreement :linkClose', {linkOpen: '<a href=\'/APIcenter_Processing_agreement.pdf\' target=\'_blank\'>', linkClose: '</a>'})"/>
      </div>
    </template>
  </ConfirmationTermsAndServiceModal>
</template>
<style>
.termsAndServiceBoxes{
  margin-top: 40px;
}
</style>